import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import Button from "./Buttons";

const EnterpriseExistingRisksDialog = ({ isOpen, onClose, onConfirm, existingRisksInfo }) => {
  const [strategy, setStrategy] = useState("clearAll");

  useEffect(() => {
    if (isOpen) setStrategy("clearAll");
  }, [isOpen]);

  if (!isOpen || !existingRisksInfo) return null;

  const {
    documentBasedRiskCount = 0,
    questionBasedRiskCount = 0,
    documentBasedRemediationCount = 0,
    questionBasedRemediationCount = 0,
  } = existingRisksInfo;

  const totalRiskCount = documentBasedRiskCount + questionBasedRiskCount;
  const totalRemediationCount = documentBasedRemediationCount + questionBasedRemediationCount;
  const showDocumentOnlyOption = questionBasedRiskCount > 0;

  const options = [
    {
      value: "clearAll",
      label: "Start fresh \u2014 clear all risks and remediations",
      description: `Remove all ${totalRiskCount} existing risk(s)${totalRemediationCount > 0 ? ` and ${totalRemediationCount} remediation(s)` : ""}${
        showDocumentOnlyOption
          ? ` (${documentBasedRiskCount} from documents, ${questionBasedRiskCount} from questionnaires)`
          : ""
      }. Only new findings from this document will remain.`,
    },
    ...(showDocumentOnlyOption
      ? [
          {
            value: "clearDocumentBased",
            label: "Replace document findings only",
            description: `Remove ${documentBasedRiskCount} document-based risk(s)${
              documentBasedRemediationCount > 0
                ? ` and ${documentBasedRemediationCount} remediation(s)`
                : ""
            }. Keep ${questionBasedRiskCount} questionnaire-based risk(s) intact. New document findings will be added.`,
          },
        ]
      : []),
    {
      value: "keepAll",
      label: "Keep everything \u2014 add new findings alongside existing",
      description: `All ${totalRiskCount} existing risk(s) remain unchanged. New findings from this document will be added alongside them.`,
    },
  ];

  const handleConfirm = () => {
    onConfirm(strategy);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Existing Assessment Results Found" size="lg">
      <div>
        <p className="text-sm text-content-secondary mb-4">
          This assessment already has {totalRiskCount} risk(s) from previous assessments. How would you
          like to handle them?
        </p>

        <div className="space-y-3 mb-6">
          {options.map((option) => (
            <label
              key={option.value}
              className={`block p-3 border rounded-lg cursor-pointer transition-colors ${
                strategy === option.value
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-border hover:border-gray-400"
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="existingRiskStrategy"
                  value={option.value}
                  checked={strategy === option.value}
                  onChange={(e) => setStrategy(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-content-primary">{option.label}</span>
                  <p className="text-sm text-content-secondary mt-1">{option.description}</p>
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Upload & Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EnterpriseExistingRisksDialog;
